import React, { useState, useEffect } from "react"
import {
    Box,
    Typography,
    TextField,
    Paper,
    CircularProgress,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Divider,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import { searchWorkers } from "../../../utils/RosterAPI"
import { toast } from "react-toastify"

export default function WorkerSearch({ projectId, onWorkerSelect }) {
    const [searchQuery, setSearchQuery] = useState("")
    const [searchResults, setSearchResults] = useState([])
    const [loading, setLoading] = useState(false)
    const [showResults, setShowResults] = useState(false)

    // Debounce search
    useEffect(() => {
        if (!searchQuery.trim()) {
            setSearchResults([])
            setShowResults(false)
            return
        }

        const timeoutId = setTimeout(() => {
            performSearch()
        }, 500) // 500ms debounce

        return () => clearTimeout(timeoutId)
    }, [searchQuery])

    const performSearch = async () => {
        if (!searchQuery.trim()) {
            setSearchResults([])
            setShowResults(false)
            return
        }

        setLoading(true)
        try {
            const response = await searchWorkers(searchQuery, projectId)
            if (response) {
                setSearchResults(response.workers || [])
                setShowResults(true)
            }
        } catch (error) {
            console.error("Error searching workers:", error)
            toast.error(error?.message || "Failed to search workers")
            setSearchResults([])
        } finally {
            setLoading(false)
        }
    }

    const handleWorkerClick = (worker) => {
        if (onWorkerSelect) {
            onWorkerSelect(worker)
        }
        setShowResults(false)
        setSearchQuery("")
    }

    return (
        <Box sx={{ position: "relative", mb: 3 }}>
            <TextField
                fullWidth
                placeholder="Search workers by name or code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: "#667085", fontSize: 20 }} />
                        </InputAdornment>
                    ),
                    endAdornment: loading && (
                        <InputAdornment position="end">
                            <CircularProgress size={20} />
                        </InputAdornment>
                    ),
                }}
                sx={{
                    "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                    },
                }}
            />

            {/* Search Results Dropdown */}
            {showResults && searchResults.length > 0 && (
                <Paper
                    elevation={3}
                    sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        mt: 1,
                        maxHeight: "400px",
                        overflowY: "auto",
                        zIndex: 1000,
                        border: "1px solid #E4E7EC",
                        borderRadius: "8px",
                    }}
                >
                    <List sx={{ p: 0 }}>
                        {searchResults.map((worker, index) => (
                            <React.Fragment key={worker.id || index}>
                                <ListItem disablePadding>
                                    <ListItemButton onClick={() => handleWorkerClick(worker)}>
                                        <ListItemText
                                            primary={`${worker.firstName} ${worker.lastName}`}
                                            secondary={
                                                <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                                                    <Typography variant="body2" sx={{ color: "#667085" }}>
                                                        {worker.speciality}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: "#667085" }}>
                                                        {worker.phoneNumber}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: "#667085" }}>
                                                        {worker.nationalId}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>
                                {index < searchResults.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                </Paper>
            )}

            {showResults && searchQuery.trim() && searchResults.length === 0 && !loading && (
                <Paper
                    elevation={3}
                    sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        mt: 1,
                        p: 2,
                        zIndex: 1000,
                        border: "1px solid #E4E7EC",
                        borderRadius: "8px",
                    }}
                >
                    <Typography sx={{ color: "#667085", fontSize: "14px", textAlign: "center" }}>
                        No workers found
                    </Typography>
                </Paper>
            )}
        </Box>
    )
}

